import algoliasearch from 'algoliasearch/lite';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Configure,
  InstantSearch,
  Pagination,
  useClearRefinements,
  useHits,
  useInstantSearch,
  usePagination,
  useRange,
  useRefinementList,
  useSearchBox,
  useSortBy
} from 'react-instantsearch';
import { useParams } from 'react-router-dom';
import { Footer } from '../../Components/Footer/Footer';
import { Header } from '../../Components/Header/Header';
import { PageHeader } from '../../Components/PageHeader/PageHeader';
import { ProductCard } from '../../Components/ProductCard/ProductCard';
import SeoDefault from '../../Components/Seo/SeoDefault';
import { useApp } from '../../context/AppContextProvider';
import { enviroment } from '../../enviroment';
import './SearchPage.css';
import styles from './SearchPage.module.css';

// Initialize Algolia client
const searchClient = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID,
  process.env.REACT_APP_ALGOLIA_APP_KEY
);

// Custom Hit component
const Hit = ({ hit }) => {
  return (
    <div className={`col-12`}>
      <ProductCard item={hit} />
    </div>
  );
};

// Panel component for filters
const Panel = ({ header, children }) => {
  return (
    <div className={styles.filterPanel}>
      <h3 className={styles.filterHeader}>{header}</h3>
      {children}
    </div>
  );
};

// Loading Component
const LoadingState = () => (
  <div className={styles.loadingContainer}>
    <div className={styles.loadingSpinner}></div>
    <p className={styles.loadingText}>Loading products...</p>
  </div>
);

// Custom SortBy Component using v7 hook
const CustomSortBy = () => {
  const { options, currentRefinement, refine } = useSortBy({
    items: [
      { label: 'Relevance', value: process.env.REACT_APP_COMPANY_ID },
      { label: 'Price: Low to High', value: `${process.env.REACT_APP_COMPANY_ID}_selling_price_asc` },
      { label: 'Price: High to Low', value: `${process.env.REACT_APP_COMPANY_ID}_selling_price_desc` },
    ],
  });

  return (
    <div className={`${styles.sortByContainer} d-flex w-100 justify-content-end`}>
      <div style={{ width: "fit-content" }}>
        <select
          className={`${styles.sortBySelect} form-select`}
          value={currentRefinement}
          onChange={(event) => refine(event.target.value)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

// Custom Hits Component using v7 hook
const CustomHits = ({ windowWidth }) => {
  const { hits, results } = useHits();
  const { status } = useInstantSearch();
  const { currentRefinement: currentPage, nbPages, nbHits } = usePagination();


  console.log('Pagination Debug:', {
    currentPage: currentPage + 1, // Convert to 1-based for display
    totalPages: nbPages,
    totalHits: nbHits,
    hitsPerPage: results?.hitsPerPage,
    query: results?.query,
  });

  if (status === 'loading' || status === 'stalled') {
    return (
      <div className="w-100">
        <LoadingState />
      </div>
    );
  }

  if (!hits.length) {
    return (
      <div className={styles.noResults}>
        <p>No results found for "{results?.query || ''}"</p>
        <p>Try adjusting your search or filters to find what you're looking for.</p>
      </div>
    );
  }

  const currentPageResults = results?.hits?.length;
  const totalFilteredResults = results?.nbHits || currentPageResults;
  const hasActiveQuery = results?.query && results?.query?.trim();

  // Debug logging to understand what's happening
  console.log('Search Results Debug:', {
    query: results?.query,
    nbHits: results?.nbHits,
    hitsLength: hits.length,
    totalFilteredResults,
    hasActiveQuery
  });

  return (
    <div className="col-12">
      {/* Results Count Display */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="results-count">
          <span className="fw-bold">
            {totalFilteredResults} result{totalFilteredResults !== 1 ? 's' : ''} found
          </span>
          {hasActiveQuery && (
            <span className="text-muted ms-2">
              for "{results.query}"
            </span>
          )}
        </div>
        {/* {results?.processingTimeMS && hasActiveQuery && (
          <div className="text-muted small">
            Found in {results.processingTimeMS}ms
          </div>
        )} */}
      </div>

      <div className="row search-page ais-Hits-list">
        {hits.map((hit) => (
          <div key={hit.objectID} className={`${styles.hitItem} ais-Hits-item`}>
            <Hit hit={hit} />
          </div>
        ))}
      </div>
    </div>
  );
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error in component:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong. Please try again.</div>;
    }
    return this.props.children;
  }
}

// Custom Searchable Refinement List Component using v7 hook
const CustomSearchableRefinementList = ({ attribute, placeholder, searchable = true }) => {
  const {
    items,
    refine,
    searchForItems,
    canToggleShowMore,
    isShowingMore,
    toggleShowMore,
  } = useRefinementList({
    attribute,
    limit: 5,
    showMore: true,
    showMoreLimit: 20,
    searchable,
  });

  const [searchTerm, setSearchTerm] = useState('');
  const isProcessingRef = useRef(false);
  const lastRefinedValueRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchTerm(value);
    searchForItems(value);
  }, [searchForItems]);

  const handleItemClick = useCallback((item) => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Prevent processing if already processing the same item
    if (isProcessingRef.current && lastRefinedValueRef.current === item.value) {
      return;
    }

    // Debounce the refinement to prevent rapid successive calls
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingRef.current = true;
      lastRefinedValueRef.current = item.value;

      try {
        refine(item.value);
      } catch (error) {
        console.error('Error refining item:', error);
      } finally {
        // Reset the flag after a delay to ensure the refinement completes
        setTimeout(() => {
          isProcessingRef.current = false;
          lastRefinedValueRef.current = null;
        }, 300);
      }
    }, 50); // Small delay to debounce rapid clicks
  }, [refine]);

  const handleToggleShowMore = useCallback(() => {
    toggleShowMore();
  }, [toggleShowMore]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={styles.customRefinementList}>
      {searchable && (
        <div className={styles.searchInputContainer}>
          <input
            type="text"
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleSearchChange}
            className={styles.searchInput}
          />
        </div>
      )}

      <div className={styles.itemsContainer}>
        {items.length > 0 ? (
          <>
            {items.map((item) => (
              <div
                key={`${attribute}-${item.value}`}
                className={`${styles.refinementItem} ${item.isRefined ? styles.selected : ''}`}
                onClick={() => handleItemClick(item)}
              >
                <div className={`${styles.checkboxContainer} d-flex align-items-center ms-2 pr-2`}>
                  <input
                    type="checkbox"
                    checked={item.isRefined}
                    onChange={() => handleItemClick(item)}
                    className={styles.customCheckbox}
                  />
                  <span className={styles.checkmark}></span>
                </div>
                <span className={styles.itemLabel}>{item.label}</span>
                <span className={styles.itemCount}>({item.count})</span>
              </div>
            ))}

            {canToggleShowMore && (
              <button
                className={styles.showMoreButton}
                onClick={handleToggleShowMore}
              >
                {isShowingMore ? 'Show less' : 'Show more'}
              </button>
            )}
          </>
        ) : (
          <div className={styles.noResults}>
            No {attribute} found for "{searchTerm}"
          </div>
        )}
      </div>
    </div>
  );
};

// Custom Non-Searchable Refinement List Component using v7 hook
const CustomRefinementList = ({ attribute }) => {
  const { items, refine } = useRefinementList({
    attribute,
    limit: 10,
  });

  const isProcessingRef = useRef(false);
  const lastRefinedValueRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const handleItemClick = useCallback((item) => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Prevent processing if already processing the same item
    if (isProcessingRef.current && lastRefinedValueRef.current === item.value) {
      return;
    }

    // Debounce the refinement to prevent rapid successive calls
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingRef.current = true;
      lastRefinedValueRef.current = item.value;

      try {
        refine(item.value);
      } catch (error) {
        console.error('Error refining item:', error);
      } finally {
        // Reset the flag after a delay to ensure the refinement completes
        setTimeout(() => {
          isProcessingRef.current = false;
          lastRefinedValueRef.current = null;
        }, 300);
      }
    }, 50); // Small delay to debounce rapid clicks
  }, [refine]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // if selling_price then sort by value
  if (attribute === 'selling_price') {
    items.sort((a, b) => a.value - b.value);
  }

  return (
    <div className={styles.customRefinementList}>
      <div className={styles.itemsContainer}>
        {items.map((item) => (
          <div
            key={`${attribute}-${item.value}`}
            className={`${styles.refinementItem} ${item.isRefined ? styles.selected : ''}`}
            onClick={() => handleItemClick(item)}
          >
            <div className={`${styles.checkboxContainer} d-flex align-items-center ms-2 pr-2`}>
              <input
                type="checkbox"
                checked={item.isRefined}
                onChange={() => handleItemClick(item)}
                className={styles.customCheckbox}
              />
              <span className={styles.checkmark}></span>
            </div>
            <span className={styles.itemLabel}>{item.label}</span>
            <span className={styles.itemCount}>({item.count})</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Custom Price Range Refinement Component
const CustomPriceRangeRefinement = ({ attribute }) => {
  const { refine: rangeRefine, currentRefinement } = useRange({ attribute });
  const { results } = useInstantSearch();
  const isProcessingRef = useRef(false);
  const lastRefinedValueRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  // Function to dynamically generate price ranges
  const generatePriceRanges = (facetData) => {
    if (!facetData || Object.keys(facetData).length === 0) {
      return [];
    }

    // Extract price points and convert to numbers
    const prices = Object.keys(facetData).map(price => parseFloat(price));
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    // Define range increment (adjustable)
    const rangeIncrement = 500;
    const ranges = [];
    let currentMin = Math.floor(minPrice / rangeIncrement) * rangeIncrement;

    // Generate ranges
    while (currentMin < maxPrice) {
      const currentMax = currentMin + rangeIncrement;
      ranges.push({
        min: currentMin,
        max: currentMax,
        label: `₹${currentMin.toLocaleString('en-IN')} - ₹${currentMax.toLocaleString('en-IN')}`,
      });
      currentMin = currentMax + 1;
    }

    // Adjust the last range to include the max price
    if (ranges.length > 0 && ranges[ranges.length - 1].max < maxPrice) {
      ranges[ranges.length - 1].max = Math.ceil(maxPrice);
      ranges[ranges.length - 1].label = `₹${ranges[ranges.length - 1].min.toLocaleString('en-IN')} - ₹${ranges[ranges.length - 1].max.toLocaleString('en-IN')}`;
    }

    return ranges;
  };

  // Aggregate counts for each range from facet data
  const getRangeItems = () => {
    const sellingPriceFacet = results?.facets?.find(facet => facet.name === attribute)?.data || {};
    const priceRanges = generatePriceRanges(sellingPriceFacet);

    return priceRanges.map(range => {
      let count = 0;
      // Sum counts for prices within the range
      Object.keys(sellingPriceFacet).forEach(price => {
        const priceNum = parseFloat(price);
        if (priceNum >= range.min && priceNum <= range.max) {
          count += sellingPriceFacet[price];
        }
      });
      console.log('refined', currentRefinement);
      console.log('range', range);
      return {
        label: range.label,
        value: `${range.min}:${range.max}`,
        count,
        min: range.min,
        max: range.max,
        isRefined: currentRefinement && range.min === currentRefinement.min && range.max === currentRefinement.max,
      };
    }).filter(item => item.count > 0); // Only show ranges with items
  };

  const rangeItems = getRangeItems(rangeRefine);

  const handleItemClick = useCallback((item) => {
    // Prevent processing if already handling the same range
    if (isProcessingRef.current && lastRefinedValueRef.current === item.value) {
      return;
    }

    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Debounce the refinement to prevent rapid successive calls
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingRef.current = true;
      lastRefinedValueRef.current = item.value;

      try {
        // Log for debugging
        console.log('Applying range:', { min: item.min, max: item.max, isRefined: item.isRefined });

        // Toggle the range filter
        if (item.isRefined) {
          // Clear the filter if already refined
          rangeRefine([0, Infinity]);
        } else {
          // Apply the new range filter
          rangeRefine([item.min, item.max]);
        }
      } catch (error) {
        console.error('Error refining range:', error);
      } finally {
        // Reset the flag after a delay to ensure the refinement completes
        setTimeout(() => {
          isProcessingRef.current = false;
          lastRefinedValueRef.current = null;
        }, 300);
      }
    }, 50); // Small delay to debounce rapid clicks
  }, [rangeRefine]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Log current refinement for debugging
  useEffect(() => {
    console.log('Current Refinement:', currentRefinement);
  }, [currentRefinement]);

  return (
    <div className={styles.customRefinementList}>
      <div className={styles.itemsContainer}>
        {rangeItems.length > 0 ? (
          rangeItems.map((item) => (
            <div
              key={`${attribute}-${item.value}`}
              className={`${styles.refinementItem} ${item.isRefined ? styles.selected : ''}`}
              onClick={() => handleItemClick(item)}
            >
              <div className={`${styles.checkboxContainer} d-flex align-items-center ms-2 pr-2`}>
                <input
                  type="checkbox"
                  checked={
                    (item.isRefined || (rangeItems?.length === 1)) ? true : false
                  }
                  onChange={() => handleItemClick(item)}
                  className={styles.customCheckbox}
                />
                <span className={styles.checkmark}></span>
              </div>
              <span className={styles.itemLabel}>{item.label}</span>
              <span className={styles.itemCount}>({item.count})</span>
            </div>
          ))
        ) : (
          <div className={styles.noResults}>
            No price ranges available
          </div>
        )}
      </div>
    </div>
  );
};

const SearchInitializer = ({ initialQuery }) => {
  const { refine } = useSearchBox();

  useEffect(() => {
    if (initialQuery && initialQuery.trim()) {
      refine(initialQuery);
    }
  }, [initialQuery, refine]);

  return null; // This component doesn't render anything
};

// Custom Clear Refinements Component
const CustomClearRefinements = () => {
  const { refine, canRefine } = useClearRefinements();

  if (!canRefine) {
    return null;
  }

  return (
    <button
      onClick={() => {
        window.location.reload();
      }}
      className={styles.resetButton}
      title="Clear all refinements"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
        <path d="M3 3v5h5" />
      </svg>
    </button>
  );
};

// Search Content Component
const SearchContent = ({ windowWidth }) => {
  const [sortPopup, setSortPopup] = useState(false);
  const [filterPopup, setFilterPopup] = useState(false);

  return (
    <>
      <div className="d-inline-flex flex-wrap col-12 ">
        <div className="d-inline-flex align-items-start col-12 gap-2">
          <div
            className={`${styles.filterSticky} hideInMobile col-3 position-sticky flex-shrink-1 d-inline-flex overflow-y-auto`}
          >
            <div className="d-flex flex-column ">
              <div className="d-inline-flex justify-content-between align-items-center">
                <p className="fw-bold fs-5 p-0 m-0 mb-2">Filters</p>
                <ErrorBoundary>
                  <CustomClearRefinements />
                </ErrorBoundary>
              </div>
              <Panel header="Category">
                <ErrorBoundary>
                  <CustomSearchableRefinementList
                    attribute="category"
                    placeholder="Search category"
                    searchable
                  />
                </ErrorBoundary>
              </Panel>
              <Panel header="Brands">
                <ErrorBoundary>
                  <CustomSearchableRefinementList
                    attribute="brand"
                    placeholder="Search brands"
                    searchable
                  />
                </ErrorBoundary>
              </Panel>
              <Panel header="Age Group">
                <ErrorBoundary>
                  <CustomSearchableRefinementList
                    attribute="age"
                    placeholder="Search age"
                    searchable
                  />
                </ErrorBoundary>
              </Panel>
              <Panel header="Gender">
                <ErrorBoundary>
                  <CustomRefinementList attribute="gender" />
                </ErrorBoundary>
              </Panel>
              <Panel header="Price Range">
                <ErrorBoundary>
                  <CustomPriceRangeRefinement attribute="selling_price" />
                </ErrorBoundary>
              </Panel>
            </div>
          </div>
          <div
            className={`${windowWidth === 'mobile' ? 'col-12' : 'col-12 col-lg-9'} position-relative pt-2`}
            style={{ maxWidth: '100%' }}
          >
            <div
              className={`${styles.productContainer} flex-shrink-1 d-inline-flex flex-wrap w-100`}
              style={{ maxWidth: '100%', minHeight: '500px' }}
            >
              {windowWidth === 'mobile' && (
                <div
                  className={`${styles.productBtnBox} hideInDesktop d-inline-flex align-items-stretch col-12 bottom-0 start-0`}
                >
                  <span
                    className={`${styles.goCartBtn} position-relative col-6 d-inline-flex align-items-center justify-content-center gap-2`}
                    onClick={() => setSortPopup(true)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18" />
                      <path d="M6 12h12" />
                      <path d="M9 18h6" />
                    </svg>
                    Sort By
                  </span>
                  <span
                    className={`${styles.AddCartBtn} position-relative col-6 d-inline-flex align-items-center justify-content-center gap-2`}
                    onClick={() => setFilterPopup(true)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46" />
                    </svg>
                    Filters
                  </span>
                </div>
              )}

              {/* {windowWidth !== 'mobile' && (
                <ErrorBoundary>
                  <CustomSortBy />
                </ErrorBoundary>
              )} */}
              {/* <ErrorBoundary> */}
              <CustomHits windowWidth={windowWidth} />
              {/* </ErrorBoundary> */}
            </div>
          </div>
        </div>
      </div>

      {filterPopup && (
        <div
          className={`${styles.filterPopup} hideInDesktop top-0 start-0 h-100 col-12 position-fixed d-inline-flex flex-column overflow-y-auto`}
        >
          <div
            className={`${styles.PageHeader} position-sticky top-0 start-0 col-12 d-inline-flex gap-2`}
          >
            <div
              className={`${styles.backBox} d-inline-flex align-items-center justify-content-center flex-shrink-0`}
              onClick={() => setFilterPopup(false)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </div>
            <div className="d-inline-flex align-items-center mw-100 flex-shrink-1 col-6 me-auto">
              <label
                className={`${styles.currentName} text-truncate col-12 d-inline-block`}
              >
                Filter
              </label>
            </div>
          </div>
          <div className="d-flex flex-column gap-3 p-3">
            <div className="d-inline-flex justify-content-between align-items-center">
              <p className="fw-bold fs-5 p-0 m-0">Filters</p>
              <ErrorBoundary>
                <CustomClearRefinements />
              </ErrorBoundary>
            </div>
            <Panel header="Category">
              <ErrorBoundary>
                <CustomSearchableRefinementList
                  attribute="category"
                  placeholder="Search category"
                  searchable
                />
              </ErrorBoundary>
            </Panel>
            <Panel header="Brands">
              <ErrorBoundary>
                <CustomSearchableRefinementList
                  attribute="brand"
                  placeholder="Search brands"
                  searchable
                />
              </ErrorBoundary>
            </Panel>
            <Panel header="Age Group">
              <ErrorBoundary>
                <CustomSearchableRefinementList
                  attribute="age"
                  placeholder="Search age"
                  searchable
                />
              </ErrorBoundary>
            </Panel>
            <Panel header="Gender">
              <ErrorBoundary>
                <CustomRefinementList attribute="gender" />
              </ErrorBoundary>
            </Panel>
            <Panel header="Price Range">
              <ErrorBoundary>
                <CustomPriceRangeRefinement attribute="selling_price" />
              </ErrorBoundary>
            </Panel>
          </div>
          <div
            className={`${styles.productBtnBox} d-inline-flex align-items-stretch col-12 position-sticky bottom-0 start-0`}
          >
            <span
              className={`${styles.saveFilterBtn} position-relative col-12 d-inline-flex align-items-center justify-content-center gap-2`}
              onClick={() => setFilterPopup(false)}
            >
              Apply Filter
            </span>
          </div>
        </div>
      )}

      {sortPopup && (
        <div
          className={`${styles.filterPopup} hideInDesktop top-0 start-0 h-100 col-12 position-fixed d-inline-flex flex-column overflow-y-auto`}
        >
          <div
            className={`${styles.PageHeader} position-sticky top-0 start-0 col-12 d-inline-flex gap-2`}
          >
            <div
              className={`${styles.backBox} d-inline-flex align-items-center justify-content-center flex-shrink-0`}
              onClick={() => setSortPopup(false)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </div>
            <div className="d-inline-flex align-items-center mw-100 flex-shrink-1 col-6 me-auto">
              <label
                className={`${styles.currentName} text-truncate col-12 d-inline-block`}
              >
                Sort By
              </label>
            </div>
          </div>
          {/* <div className="d-flex flex-column gap-3 p-3">
            <ErrorBoundary>
              <CustomSortBy />
            </ErrorBoundary>
          </div> */}
          <div
            className={`${styles.productBtnBox} d-inline-flex align-items-stretch col-12 position-sticky bottom-0 start-0`}
          >
            <span
              className={`${styles.saveFilterBtn} position-relative col-12 d-inline-flex align-items-center justify-content-center gap-2`}
              onClick={() => setSortPopup(false)}
            >
              Apply Sort
            </span>
          </div>
        </div>
      )}
    </>
  );
};

// Search Page Component
export const SearchPage = () => {
  const { keyword } = useParams();
  const appData = useApp();
  const windowWidth = appData.appData.windowWidth;

  return (
    <React.Fragment>
      <SeoDefault
        title={`Search "${keyword}" - Knick Knack`}
        description={`Search results for "${keyword}" at Knick Knack. Find premium children's toys and games with free delivery.`}
        keywords={`${keyword}, Knick Knack, toys, children's games, buy online`}
        canonicalUrl={`https://knickknack.online/search-product/${keyword}`}
        pageType="website"
        breadcrumbItems={[
          { name: 'Home', url: 'https://knickknack.online/' },
          { name: `Search "${keyword}"`, url: `https://knickknack.online/search-product/${keyword}` },
        ]}
      />
      {windowWidth === 'mobile' ? (
        <PageHeader title={`Results for ${keyword}`} />
      ) : windowWidth === 'desktop' ? (
        <Header />
      ) : (
        ''
      )}
      <div className={`col-12 d-inline-flex ${windowWidth === 'mobile' ? 'mt-3' : 'mt-5'}`}>
        <div className="container">
        <InstantSearch
            searchClient={searchClient}
            indexName={process.env.REACT_APP_COMPANY_ID}
            initialUiState={{
              [process.env.REACT_APP_COMPANY_ID]: {
                query: keyword,
                page: 0,
                hitsPerPage: 60,
                refinementList: {},
                range: {},
                sortBy: process.env.REACT_APP_COMPANY_ID,
              },
            }}
            future={{
              preserveSharedStateOnUnmount: true,
            }}
          >
            <Configure
              filters={`stock > 0 AND store_id = ${enviroment.STORE_ID} AND is_active = 1 AND is_delete = 1`}
              restrictSearchableAttributes={['name', 'brand', 'category']}
              attributesToRetrieve={['product_id', 'id', 'photo', 'stock', 'is_active', 'is_delete', 'brand', 'name', 'color', 'mrp', 'selling_price', 'category', 'name_url', 'age', 'gender']}
              attributesToHighlight={['name', 'brand', 'category']}
              typoTolerance="min"
              hitsPerPage={60}
              facets={['color', 'brand', 'selling_price', 'category', 'age', 'gender']}
              distinct={true}
              advancedSyntax={true}
              removeStopWords={true}
              queryType="prefixLast"
              minWordSizefor1Typo={4}
              minWordSizefor2Typos={8}
              minProximity={2}
              ranking={['exact', 'typo', 'words', 'proximity', 'attribute', 'filters', 'custom']}
              optionalFilters={[`brand:${keyword}`, `name:${keyword}`, `category:${keyword}`]}
              removeWordsIfNoResults="allOptional"
              exactOnSingleWordQuery="attribute"
              alternativesAsExact={['ignorePlurals', 'singleWordSynonym']}
              ignorePlurals={true}
              queryLanguages={['en']}
            />
            <SearchInitializer initialQuery={keyword} />
            <SearchContent windowWidth={windowWidth} />
            <div className="col-12 d-flex justify-content-center mt-4 bg-white rounded" style={{
              transform: 'translateY(-10px)',
            }}>
              <Pagination
                classNames={{
                  root: styles.pagination,
                  list: styles.paginationList,
                  item: styles.paginationItem,
                  selectedItem: styles.paginationSelectedItem,
                  disabledItem: styles.paginationDisabledItem,
                  link: styles.paginationLink,
                }}
                translations={{
                  previousPageItemAriaLabel: 'Previous page',
                  nextPageItemAriaLabel: 'Next page',
                  pageItemAriaLabel: (pageNumber) => `Page ${pageNumber}`,
                  firstPageItemAriaLabel: 'First page',
                  lastPageItemAriaLabel: 'Last page',
                }}
              />
            </div>
          </InstantSearch>
        </div>
      </div>
      <Footer />
    </React.Fragment>
  );
};